import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BookOpen, Copy, Flag, Search, ShieldAlert, X } from 'lucide-react'
import { useCapabilities } from '../hooks/useCapabilities'
import { useGetManualIndex, useGetManualPart, useGetManualSearch } from '../hooks/backend/discipleship'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { toast } from '../lib/shadcn/sonner'
import { cn } from '../lib/shadcn/utils'
import { ManualMarkdown } from '../components/discipleship/ManualMarkdown'
import { ReportProblemDialog } from '../components/platform/ReportProblemDialog'
import { partOfSectionKey, sectionAnchor, sectionReference } from '../utils/manualMarkdown'

type IndexSection = { key: string; heading: string; level: number; kind: string; parent_key: string | null }
type IndexPart = { part_number: number; title: string; owner_text: string | null; sections: IndexSection[] }
type ManualIndex = { ok: true; edition: { label: string; position_date: string }; parts: IndexPart[] }
type PartSection = IndexSection & { body_md: string; media: { id: string; caption: string }[] }
type ManualPart = {
  ok: true
  edition: string
  part_number: number
  title: string
  owner_text: string | null
  main_readers: string | null
  sections: PartSection[]
}
type SearchHit = { key: string; heading: string; part_number: number; snippet: string }

function partLabel(n: number, title: string): string {
  return n === 0 ? 'Introduction' : title.replace(/^Part \d+: /, `Part ${n}: `)
}

/**
 * The Fleetly Platform Manual, inside the cockpit (docs/DISCIPLESHIP.md).
 *
 * The text is the approved edition loaded by migration 4601 and is shown as it
 * was written: nothing on this page edits, summarises or reorders it. A wrong
 * section is reported from the section itself, through the same Report a problem
 * flow as any other fault, with the section reference already filled in.
 *
 * The address carries the section (?section=2.4.leave), so a reference copied
 * from here opens the same place for whoever it is sent to.
 */
export default function Manual() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const index = useGetManualIndex<ManualIndex>()
  const part = useGetManualPart<ManualPart>()
  const search = useGetManualSearch<{ ok: true; hits: SearchHit[] }>()
  const [q, setQ] = useState('')
  // The words the shown result answers. Until it matches what is typed, the
  // count is not shown: "0 sections" while the search has not yet run is a
  // false statement, not a pause.
  const [searchedFor, setSearchedFor] = useState('')
  const [report, setReport] = useState<string | null>(null)

  const sectionParam = params.get('section')
  const partParam = params.get('part')
  const selectedPart = useMemo(() => {
    if (sectionParam) return partOfSectionKey(sectionParam) ?? 0
    const n = Number(partParam)
    return Number.isInteger(n) && n >= 0 && n <= 8 ? n : 0
  }, [sectionParam, partParam])

  const indexTrigger = index.trigger
  const partTrigger = part.trigger
  const searchTrigger = search.trigger
  useEffect(() => {
    if (canView) indexTrigger({})
  }, [canView, indexTrigger])
  useEffect(() => {
    if (canView) partTrigger({ part: selectedPart })
  }, [canView, selectedPart, partTrigger])

  // Scroll to the section once its Part has rendered.
  useEffect(() => {
    if (!sectionParam || part.data?.part_number !== selectedPart) return
    const el = document.getElementById(sectionAnchor(sectionParam))
    if (el) el.scrollIntoView({ block: 'start' })
  }, [sectionParam, selectedPart, part.data])

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) return
    const t = setTimeout(() => {
      searchTrigger({ q: term })
        .then(() => setSearchedFor(term))
        .catch(() => setSearchedFor(term))
    }, 250)
    return () => clearTimeout(t)
  }, [q, searchTrigger])

  const goSection = useCallback(
    (key: string) => {
      setQ('')
      setParams({ section: key })
    },
    [setParams],
  )
  const goPart = useCallback((n: number) => setParams({ part: String(n) }), [setParams])

  const edition = index.data?.edition.label ?? part.data?.edition ?? ''
  const copyReference = useCallback(
    async (s: PartSection) => {
      const text = sectionReference(edition, s.heading, window.location.origin, s.key)
      try {
        await navigator.clipboard.writeText(text)
        toast.success('Reference copied.')
      } catch {
        toast.error('Your browser did not allow copying. The reference is: ' + text)
      }
    },
    [edition],
  )

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }
  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--brand))]" />
          <p className="text-sm font-medium text-foreground">The manual is available to signed-in Fleetly staff only.</p>
          <p className="text-xs text-muted-foreground">If you should see it, ask Luyanda Jafta to give your login view access.</p>
        </div>
      </div>
    )
  }

  const parts = index.data?.parts ?? []
  const current = parts.find((p) => p.part_number === selectedPart)
  const showingSearch = q.trim().length >= 2
  const hits = searchedFor === q.trim() ? (search.data?.hits ?? []) : []

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <BookOpen className="h-5 w-5 text-[hsl(var(--brand))]" />
          <h1 className="text-lg font-semibold">Fleetly Platform Manual</h1>
          {index.data && (
            <span className="text-xs text-muted-foreground">
              Edition {index.data.edition.label}, as at {index.data.edition.position_date}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
        <div className="relative mt-3 max-w-xl">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the manual"
            aria-label="Search the manual"
            className="pl-8 pr-8"
          />
          {q && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQ('')}
              className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Below lg the Part list is a row of buttons rather than a column. */}
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
          {parts.map((p) => (
            <button
              key={p.part_number}
              type="button"
              onClick={() => goPart(p.part_number)}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-xs',
                p.part_number === selectedPart
                  ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                  : 'border-border text-muted-foreground',
              )}
            >
              {p.part_number === 0 ? 'Introduction' : `Part ${p.part_number}`}
            </button>
          ))}
        </div>
      </div>

      {index.error && <p className="px-6 py-4 text-sm text-destructive">{index.error}</p>}

      <div className="flex min-h-0 flex-1">
        <nav aria-label="Manual contents" className="hidden w-72 shrink-0 overflow-y-auto border-r border-border p-3 lg:block">
          {parts.map((p) => (
            <div key={p.part_number} className="mb-1">
              <button
                type="button"
                onClick={() => goPart(p.part_number)}
                className={cn(
                  'w-full rounded-md px-2 py-1.5 text-left text-sm font-medium',
                  p.part_number === selectedPart ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]' : 'hover:bg-accent',
                )}
              >
                {partLabel(p.part_number, p.title)}
              </button>
              {p.part_number === selectedPart && (
                <ul className="mt-1 space-y-0.5 border-l border-border pl-2">
                  {p.sections.map((s) => (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => goSection(s.key)}
                        className={cn(
                          'w-full truncate rounded px-2 py-0.5 text-left text-xs hover:bg-accent',
                          s.level === 3 && 'pl-4 text-muted-foreground',
                          sectionParam === s.key && 'font-semibold text-[hsl(var(--brand))]',
                        )}
                        title={s.heading}
                      >
                        {s.heading}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </nav>

        <main className="min-w-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {showingSearch ? (
            <div className="max-w-3xl">
              <p className="mb-3 text-xs text-muted-foreground">
                {search.loading || searchedFor !== q.trim()
                  ? 'Searching…'
                  : search.error
                    ? search.error
                    : `${hits.length} section${hits.length === 1 ? '' : 's'} contain every word.`}
                {searchedFor === q.trim() && hits.length === 40 && ' Showing the first 40; add a word to narrow it.'}
              </p>
              <ul className="space-y-2">
                {hits.map((h) => (
                  <li key={h.key}>
                    <button
                      type="button"
                      onClick={() => goSection(h.key)}
                      className="w-full rounded-md border border-border p-3 text-left hover:bg-accent"
                    >
                      <span className="text-xs text-muted-foreground">
                        {h.part_number === 0 ? 'Introduction' : `Part ${h.part_number}`}
                      </span>
                      <span className="block text-sm font-medium">{h.heading}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">…{h.snippet}…</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : part.loading && !part.data ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" /> Loading the manual…
            </div>
          ) : part.error ? (
            <p className="text-sm text-destructive">{part.error}</p>
          ) : part.data ? (
            <article className="max-w-3xl">
              <h2 className="text-xl font-semibold">{partLabel(part.data.part_number, part.data.title)}</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Owner: {part.data.owner_text ?? current?.owner_text ?? 'not stated in the manual'}
                {part.data.main_readers ? `. Main readers: ${part.data.main_readers}` : ''}
              </p>
              <div className="mt-4 space-y-6">
                {part.data.sections.map((s) => (
                  <section key={s.key} id={sectionAnchor(s.key)} className="scroll-mt-4">
                    {s.level === 2 ? (
                      <h3 className="border-b border-border pb-1 text-base font-semibold">{s.heading}</h3>
                    ) : (
                      <h4 className="text-sm font-semibold">{s.heading}</h4>
                    )}
                    {s.body_md && (
                      <div className="mt-2">
                        <ManualMarkdown body={s.body_md} />
                      </div>
                    )}
                    {s.media.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {s.media.map((m) => (
                          <li key={m.id}>Screenshot: {m.caption}</li>
                        ))}
                      </ul>
                    )}
                    {(s.body_md || s.level === 3) && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span>
                          Edition {part.data!.edition}. Owner: {part.data!.owner_text ?? 'not stated in the manual'}
                        </span>
                        <button type="button" onClick={() => copyReference(s)} className="inline-flex items-center gap-1 hover:text-foreground">
                          <Copy className="h-3 w-3" /> Copy reference
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setReport(
                              `Something is wrong in the manual, ${sectionReference(part.data!.edition, s.heading, window.location.origin, s.key)}\n\nWhat is wrong: `,
                            )
                          }
                          className="inline-flex items-center gap-1 text-[hsl(var(--brand))] hover:underline"
                        >
                          <Flag className="h-3 w-3" /> Report something wrong in this section
                        </button>
                      </div>
                    )}
                  </section>
                ))}
              </div>
              <button
                type="button"
                onClick={() => navigate('/manual?part=' + Math.min(8, selectedPart + 1))}
                className={cn('mt-8 text-sm text-[hsl(var(--brand))] hover:underline', selectedPart === 8 && 'hidden')}
              >
                Next: {parts.find((p) => p.part_number === selectedPart + 1)?.title ?? ''}
              </button>
            </article>
          ) : null}
        </main>
      </div>
      <ReportProblemDialog open={report !== null} onOpenChange={(o) => !o && setReport(null)} prefill={report ?? undefined} />
    </div>
  )
}
